import {
  AwxRfiStatus,
  AwxLinkedAccountStatus,
  OrganizationStateType,
  AwxConnectedAccountStatus,
} from "@common/utils/graphql";
import { atom, selector } from "recoil";

export const SUCCESSFUL_LINKED_STATUSES = new Set([
  AwxLinkedAccountStatus.Succeeded,
  AwxLinkedAccountStatus.Pending,
  AwxLinkedAccountStatus.Processing,
]);

// Type guard to check if the organization state is not null
const hasOrganizationState = (
  state: OrganizationStateType | null,
): state is OrganizationStateType => state !== null;

// Helper function to check if the status is successful
const isStatusSuccessful = (
  status: string | undefined | null,
  successfulStatuses: Set<string>,
) => successfulStatuses.has(status || "");

export const awxOrganizationStateAtom = atom<OrganizationStateType | null>({
  key: "awxOrganizationStateAtom", // unique ID for this atom
  default: null, // Initial default state
});

export const startKYC = selector({
  key: "startKYC",
  get: ({ get }) => {
    const awxOrganizationState = get(awxOrganizationStateAtom);

    if (!hasOrganizationState(awxOrganizationState)) {
      return true;
    }

    return (
      awxOrganizationState.connectedAccountStatus ===
      AwxConnectedAccountStatus.Created
    );
  },
});

// Selector to determine if action is needed
export const needsAction = selector({
  key: "needsAction",
  get: ({ get }) => {
    const awxOrganizationState = get(awxOrganizationStateAtom);

    if (!hasOrganizationState(awxOrganizationState)) {
      return true; // Action needed if state is null
    }

    const { rfiStatus, linkedAccountStatus } =
      get(awxOrganizationStateAtom) || {};

    return (
      rfiStatus === AwxRfiStatus.ActionRequired ||
      !isStatusSuccessful(linkedAccountStatus, SUCCESSFUL_LINKED_STATUSES)
    );
  },
});

export const isEligibleToAddBeneficiary = selector({
  key: "isEligibleToAddBeneficiary",
  get: ({ get }) => {
    const awxOrganizationState = get(awxOrganizationStateAtom);

    if (!hasOrganizationState(awxOrganizationState)) {
      return false;
    }

    return awxOrganizationState.isEligibleToAddBeneficiary === true;
  },
});
